// src/mobile_pages/quality/components/MobileQualityPCView.jsx
// 모바일 전용 품질 공정검사 뷰 (테이블 대신 카드 형식)

import React from "react";
import { Calendar, ClipboardList, CheckCircle, FileText } from "lucide-react";

export default function MobileQualityPCView({ data }) {
    if (!data) return null;

    const header = data.header || {};
    const items = data.items || [];

    // Styles by judgment
    const getResultStyle = (result) => {
        const r = (result || "").toLowerCase();
        if (r.includes("합격") || r.includes("pass") || r.includes("적합")) {
            return "bg-emerald-50 text-emerald-700 border-emerald-200";
        }
        if (r.includes("불합격") || r.includes("fail") || r.includes("부적합")) {
            return "bg-red-50 text-red-700 border-red-200";
        }
        return "bg-slate-50 text-slate-600 border-slate-200";
    };

    return (
        <div className="space-y-4">

            {/* 기본 정보 카드 */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700">기본 정보</h3>
                </div>
                <div className="p-4 space-y-3">
                    {/* 제목 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <FileText size={16} className="text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">제목</p>
                            <p className="text-sm text-slate-900 font-medium break-words">
                                {header.title || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 검사일 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <Calendar size={16} className="text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">검사일</p>
                            <p className="text-sm text-slate-900 font-medium">
                                {header.doc_date || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 공정명 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                            <ClipboardList size={16} className="text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">공정명</p>
                            <p className="text-sm text-slate-900 font-medium break-words">
                                {header.process_name || "-"}
                            </p>
                        </div>
                    </div>

                    {/* LOT */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-slate-500">LOT</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">LOT 번호</p>
                            <p className="text-sm text-slate-900 font-medium">
                                {header.lot_no || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 검사방법 */}
                    {header.method && (
                        <div className="pt-2 border-t border-slate-100">
                            <p className="text-xs text-slate-400 mb-1">검사방법</p>
                            <p className="text-sm text-slate-700 break-words">
                                {header.method}
                            </p>
                        </div>
                    )}

                    {/* 합격기준 */}
                    {header.acceptance_criteria && (
                        <div className="pt-2 border-t border-slate-100">
                            <p className="text-xs text-slate-400 mb-1">합격기준</p>
                            <p className="text-sm text-slate-700 break-words">
                                {header.acceptance_criteria}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* 검사 항목 */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">검사 항목</h3>
                    <span className="text-xs text-slate-400">{items.length}개</span>
                </div>

                {items.length === 0 ? (
                    <div className="p-6 text-center">
                        <p className="text-sm text-slate-400">검사 항목이 없습니다.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {items.map((row, idx) => (
                            <div key={idx} className="p-4">
                                {/* 항목명 + 판정 */}
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <CheckCircle size={16} className="text-slate-400 flex-shrink-0" />
                                        <span className="text-sm font-medium text-slate-900 break-words">
                                            {row.item_name || `항목 ${idx + 1}`}
                                        </span>
                                    </div>
                                    <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border flex-shrink-0 ${getResultStyle(row.result_label)}`}>
                                        {row.result_label || "-"}
                                    </span>
                                </div>

                                {/* 기준 */}
                                {row.criteria && (
                                    <div className="ml-6 mb-1">
                                        <span className="text-xs text-slate-400">기준: </span>
                                        <span className="text-xs text-slate-600 break-words">
                                            {row.criteria}
                                        </span>
                                    </div>
                                )}

                                {/* 비고 */}
                                {row.remark && (
                                    <div className="ml-6">
                                        <span className="text-xs text-slate-400">비고: </span>
                                        <span className="text-xs text-slate-600 break-words">
                                            {row.remark}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
